import { displayFeeLabel } from "../../checkout/fee-label";
import { consume } from "@lit/context";
import {
  CommerceDecisionStatus,
  CommerceIntentStatus,
  CommerceMoneyLineKind,
  CommerceOfferingKind,
  CommerceOptionState,
  CommercePaymentActionKind,
  CommercePaymentOptionKind,
  CommerceRequirementKind,
  CommerceRequirementState,
  CommerceRequestedAction,
  CommerceTerminalResultKind,
  type CommerceActionRequiredResultDto,
  type CommerceAppointmentTimeslotSelectionDto,
  type CommerceFormRequirementDto,
  type CommerceGiftDetailsRequirementDto,
  type CommerceIntentDto,
  type CommerceLegalAcceptanceRequirementDto,
  type CommerceMoneyLineDto,
  type CommerceQuoteDtoPaymentOptionsInner,
  type CommerceQuoteDtoRequirementsInner,
  type CommerceRequirementResponseDto,
  type CreateCommerceQuoteDtoOffering,
} from "@zipper/customer-commerce-client";
import {
  LitElement,
  html,
  nothing,
  type PropertyValues,
  type TemplateResult,
} from "lit";
import { customElement, property } from "lit/decorators.js";
import type { EmbedStripe } from "../../config/types";
import { StripeDomPaymentAdapter } from "../../payments/stripe-adapter";
import {
  createPublicEmbedRuntime,
  embedRuntimeContext,
  type EmbedRuntime,
} from "../../runtime/context";
import type { CheckoutRuntimeIdentity } from "../../checkout/actors";
import { commerceCheckoutStyles } from "./commerce-checkout/commerce-checkout.styles";
import { CommerceCheckoutController } from "./commerce-checkout/commerce-checkout.controller";

export function isLegalAcceptanceRequirement(
  requirement: CommerceQuoteDtoRequirementsInner,
): requirement is CommerceLegalAcceptanceRequirementDto {
  return (
    requirement.kind === CommerceRequirementKind.LegalAcceptance &&
    "contentHash" in requirement &&
    typeof requirement.contentHash === "string" &&
    "label" in requirement &&
    typeof requirement.label === "string"
  );
}

/**
 * ACH needs the buyer's debit authorization and settlement notice on screen
 * before they pay — whether connecting a new bank or using a saved one.
 */
export function achAuthorizationRequired(
  options: readonly CommerceQuoteDtoPaymentOptionsInner[],
): boolean {
  return options.some(
    (option) =>
      option.kind === CommercePaymentOptionKind.Ach &&
      option.state === CommerceOptionState.Selected,
  );
}

/**
 * Order-summary rows. Tax is left out: it renders once, from `price.tax`,
 * even when the quote also lists it as a price line.
 */
export function summaryPriceLines(
  lines: CommerceMoneyLineDto[],
): CommerceMoneyLineDto[] {
  return lines.filter((line) => line.kind !== CommerceMoneyLineKind.Tax);
}

export interface ZipperCommerceSuccessDetail {
  intent: CommerceIntentDto;
  intentId: string;
  correlationId: string;
  fulfillments: CommerceIntentDto["fulfillments"];
}

export interface ZipperCommerceStateDetail {
  stage: string;
  offeringKind: CommerceOfferingKind;
  intentId?: string;
}

export interface ZipperCommerceCancelDetail {
  reason: "customer_cancelled";
  intentId?: string;
  correlationId?: string;
}

export interface ZipperCommerceErrorDetail {
  code: string;
  message: string;
  correlationId?: string;
}

export interface ZipperCommerceAuthRequiredDetail {
  resumable: boolean;
  intentId?: string;
}

@customElement("zipper-commerce-checkout")
export class ZipperCommerceCheckout extends LitElement {
  static styles = commerceCheckoutStyles;

  @consume({ context: embedRuntimeContext, subscribe: true })
  @property({ attribute: false })
  runtime: EmbedRuntime = createPublicEmbedRuntime();

  @property({ attribute: false })
  offering?: CreateCommerceQuoteDtoOffering;

  @property({ attribute: false })
  timeslots: readonly CommerceAppointmentTimeslotSelectionDto[] = [];

  @property({ type: String })
  requestedAction: CommerceRequestedAction = CommerceRequestedAction.Purchase;

  @property({ type: Number }) orgId = 0;
  @property({ type: String }) title = "Checkout";
  @property({ type: String }) subtitle = "";
  @property({ type: String }) requestedTargetKey = "";
  @property({ type: Number }) quantity = 1;
  @property({ type: Number }) customAmountMinor?: number;
  @property({ type: Boolean }) allowCustomAmount = false;
  @property({ attribute: false }) stripe?: EmbedStripe;

  private readonly commerce = new CommerceCheckoutController(this);
  private readonly payment = new StripeDomPaymentAdapter({
    onStatus: (status) => {
      if (
        status.ready &&
        this.commerce.checkoutSnapshot?.matches({
          awaitingExternalAction: "preparingProvider",
        })
      ) {
        this.commerce.send({ type: "PAYMENT.PROVIDER_READY" });
      }
      this.requestUpdate();
    },
  });
  private mountedActionKey = "";
  private lifecycleAuthorityKey = "";
  private completedIntentId = "";
  private emittedState = "";
  private emittedCancel = false;
  private emittedError = "";
  private emittedAuth = false;

  protected willUpdate(changed: PropertyValues<this>): void {
    if (
      changed.has("runtime") ||
      changed.has("offering") ||
      changed.has("orgId") ||
      changed.has("requestedAction") ||
      changed.has("requestedTargetKey") ||
      changed.has("quantity") ||
      changed.has("customAmountMinor") ||
      changed.has("timeslots")
    ) {
      this.openCommerce();
    }
  }

  protected updated(): void {
    this.emitLifecycle();
    this.emitCompletion();
    void this.mountProviderAction();
  }

  private emitLifecycle(): void {
    const surface = this.commerce.snapshot;
    const checkout = this.commerce.checkoutSnapshot;
    if (!surface || !this.offering) return;
    const stage = surface.matches("checkout")
      ? String(checkout?.value ?? "starting")
      : String(surface.value);
    const intent = checkout?.context.intent ?? surface.context.cancelledIntent;
    const stateKey = `${stage}:${intent?.id ?? ""}`;
    if (stateKey !== this.emittedState) {
      this.emittedState = stateKey;
      const detail: ZipperCommerceStateDetail = {
        stage,
        offeringKind: this.offering.kind,
        ...(intent?.id ? { intentId: intent.id } : {}),
      };
      this.emit("zipper-commerce-state", detail);
    }
    if (surface.matches("cancelled") && !this.emittedCancel) {
      this.emittedCancel = true;
      const cancelled = surface.context.cancelledIntent;
      const detail: ZipperCommerceCancelDetail = {
        reason: "customer_cancelled",
        ...(cancelled?.id ? { intentId: cancelled.id } : {}),
        ...(cancelled?.correlationId
          ? { correlationId: cancelled.correlationId }
          : {}),
      };
      this.emit("zipper-commerce-cancel", detail);
    }
    const failure = checkout?.context.failure ?? surface.context.failure;
    const failureKey = failure ? `${failure.code}:${failure.message}` : "";
    if (failure && failureKey !== this.emittedError) {
      this.emittedError = failureKey;
      const detail: ZipperCommerceErrorDetail = {
        code: failure.code,
        message: failure.message,
        ...(intent?.correlationId
          ? { correlationId: intent.correlationId }
          : {}),
      };
      this.emit("zipper-commerce-error", detail);
    } else if (!failure) {
      this.emittedError = "";
    }
    const decision = checkout?.context.quote?.decision;
    if (
      decision?.status === CommerceDecisionStatus.AuthenticationRequired &&
      !this.emittedAuth
    ) {
      this.emittedAuth = true;
      const detail: ZipperCommerceAuthRequiredDetail = {
        resumable: true,
        ...(intent?.id ? { intentId: intent.id } : {}),
      };
      this.emit("zipper-commerce-auth-required", detail);
    } else if (
      decision?.status !== CommerceDecisionStatus.AuthenticationRequired
    ) {
      this.emittedAuth = false;
    }
  }

  private emit<T>(name: string, detail: T): void {
    this.dispatchEvent(
      new CustomEvent(name, {
        detail,
        bubbles: true,
        composed: true,
      }),
    );
  }

  disconnectedCallback(): void {
    this.payment.teardown();
    super.disconnectedCallback();
  }

  private runtimeIdentity(): CheckoutRuntimeIdentity {
    return {
      orgId: this.orgId,
      surface: this.runtime.surface,
      audience:
        this.runtime.audience ?? `${this.runtime.surface}:${this.orgId}`,
      viewerRevision:
        this.runtime.viewerRevision ??
        `${this.runtime.customer?.userId ?? "guest"}:${
          this.runtime.customer?.rosterId ?? "none"
        }`,
      ...(this.runtime.customer ? { customer: this.runtime.customer } : {}),
    };
  }

  private openCommerce(): void {
    if (!this.offering || !this.orgId || !this.runtime.commerce) return;
    const runtime = this.runtimeIdentity();
    const launch = {
      runtime,
      gateway: this.runtime.commerce,
      offering: this.offering,
      requestedAction: this.requestedAction,
      ...(this.requestedTargetKey
        ? { requestedTargetKey: this.requestedTargetKey }
        : {}),
      quantity: Math.max(1, this.quantity),
      ...(this.customAmountMinor !== undefined
        ? { customAmountMinor: this.customAmountMinor }
        : {}),
      ...(this.timeslots.length ? { timeslots: this.timeslots } : {}),
    };
    const authorityKey = JSON.stringify({
      runtime: {
        orgId: runtime.orgId,
        surface: runtime.surface,
        audience: runtime.audience,
        viewerRevision: runtime.viewerRevision,
        customer: runtime.customer,
      },
      offering: launch.offering,
      requestedAction: launch.requestedAction,
      requestedTargetKey: launch.requestedTargetKey ?? null,
      quantity: launch.quantity,
      customAmountMinor: launch.customAmountMinor ?? null,
      timeslots: launch.timeslots ?? [],
    });
    if (authorityKey !== this.lifecycleAuthorityKey) {
      this.lifecycleAuthorityKey = authorityKey;
      this.payment.teardown();
      this.mountedActionKey = "";
      this.completedIntentId = "";
      this.emittedState = "";
      this.emittedCancel = false;
      this.emittedError = "";
      this.emittedAuth = false;
    }
    this.commerce.open(launch);
  }

  private emitCompletion(): void {
    const surface = this.commerce.snapshot;
    const intent = surface?.context.completedIntent;
    if (
      !surface?.matches("succeeded") ||
      !intent ||
      this.completedIntentId === intent.id
    ) {
      return;
    }
    this.completedIntentId = intent.id;
    const detail: ZipperCommerceSuccessDetail = {
      intent,
      intentId: intent.id,
      correlationId: intent.correlationId,
      fulfillments: intent.fulfillments,
    };
    this.dispatchEvent(
      new CustomEvent("zipper-commerce-success", {
        detail,
        bubbles: true,
        composed: true,
      }),
    );
    const completion = this.runtime.onComplete?.({
      kind:
        intent.fulfillments[0]?.kind === "product" ||
        intent.fulfillments[0]?.kind === "gift"
          ? "purchase"
          : intent.fulfillments[0]?.kind === "appointment"
            ? "booking"
            : "registration",
      intentId: intent.id,
      correlationId: intent.correlationId,
      canonicalResourceKeys: intent.fulfillments
        .map(({ canonicalResourceKey }) => canonicalResourceKey)
        .filter((key): key is string => !!key),
    });
    if (completion) {
      void Promise.resolve(completion).catch((error) => {
        this.dispatchEvent(
          new CustomEvent("zipper-commerce-refresh-failed", {
            detail: {
              intentId: intent.id,
              message: error instanceof Error ? error.message : String(error),
            },
            bubbles: true,
            composed: true,
          }),
        );
      });
    }
  }

  private actionRequired(): CommerceActionRequiredResultDto["action"] | null {
    const intent = this.commerce.checkoutSnapshot?.context.intent;
    const result = intent?.result;
    return result?.kind === CommerceTerminalResultKind.ActionRequired &&
      "action" in result
      ? result.action
      : null;
  }

  private async mountProviderAction(): Promise<void> {
    const action = this.actionRequired();
    const checkout = this.commerce.checkoutSnapshot;
    if (
      !action ||
      !("clientSecret" in action) ||
      !checkout?.matches({
        awaitingExternalAction: "preparingProvider",
      })
    ) {
      return;
    }
    if (!this.stripe) {
      this.commerce.send({
        type: "PAYMENT.PROVIDER_FAILED",
        message: "Payment is unavailable for this checkout.",
        retryable: false,
      });
      return;
    }
    const key = `${this.commerce.checkoutSnapshot?.context.intent?.id}:${action.clientSecret}`;
    if (this.mountedActionKey === key) {
      this.commerce.send({ type: "PAYMENT.PROVIDER_READY" });
      return;
    }
    const target = this.renderRoot.querySelector<HTMLElement>(
      "#commerce-payment-element",
    );
    if (!target) return;
    try {
      await this.payment.initialize(this.stripe);
      this.payment.mountPaymentElement(target, action.clientSecret);
      this.mountedActionKey = key;
    } catch (error) {
      this.commerce.send({
        type: "PAYMENT.PROVIDER_FAILED",
        message:
          error instanceof Error ? error.message : "Payment is unavailable",
      });
    }
  }

  private async confirmProviderAction(): Promise<void> {
    const action = this.actionRequired();
    const checkout = this.commerce.checkoutSnapshot;
    if (
      !action ||
      !("clientSecret" in action) ||
      !checkout?.matches({
        awaitingExternalAction: "providerReady",
      })
    ) {
      return;
    }
    this.commerce.send({ type: "PAYMENT.CONFIRMING" });
    try {
      const confirmationMode =
        "confirmationMode" in action && action.confirmationMode
          ? action.confirmationMode
          : action.clientSecret.startsWith("pi_")
            ? "payment"
            : "setup";
      const paymentMethodToken = await this.payment.confirmAction({
        clientSecret: action.clientSecret,
        returnUrl: window.location.href,
        mode: confirmationMode,
      });
      this.commerce.send({
        type: "PAYMENT.RESULT",
        result: {
          status: "succeeded",
          ...(paymentMethodToken ? { paymentMethodToken } : {}),
        },
      });
    } catch (error) {
      this.commerce.send({
        type: "PAYMENT.RESULT",
        result: {
          status: "failed",
          message:
            error instanceof Error
              ? error.message
              : "Payment could not be confirmed",
        },
      });
    }
  }

  private formResponse(
    requirement: CommerceFormRequirementDto,
  ): CommerceRequirementResponseDto | null {
    const root = this.renderRoot.querySelector<HTMLElement>(
      `[data-form-key="${requirement.requirementKey}"]`,
    );
    if (!root) return null;
    const answers = requirement.questions.map((question) => {
      const fields = Array.from(
        root.querySelectorAll<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >(`[name="${question.questionKey}"]`),
      );
      const value =
        question.kind === "multi-select"
          ? fields
              .filter(
                (field): field is HTMLInputElement =>
                  field instanceof HTMLInputElement && field.checked,
              )
              .map((field) => field.value)
              .join(",")
          : (fields[0]?.value.trim() ?? "");
      return { questionKey: question.questionKey, value };
    });
    const complete = requirement.questions.every(
      (question) =>
        !question.required ||
        answers.some(
          (answer) =>
            answer.questionKey === question.questionKey && !!answer.value,
        ),
    );
    return complete
      ? {
          requirementKey: requirement.requirementKey,
          version: requirement.version,
          value: { answers },
        }
      : null;
  }

  private updateForm(requirement: CommerceFormRequirementDto): void {
    const response = this.formResponse(requirement);
    this.commerce.send(
      response
        ? { type: "REQUIREMENT.ANSWERED", response }
        : {
            type: "REQUIREMENT.CLEARED",
            requirementKey: requirement.requirementKey,
          },
    );
  }

  private updateGiftDetails(
    requirement: CommerceGiftDetailsRequirementDto,
  ): void {
    const root = this.renderRoot.querySelector<HTMLElement>(
      `[data-gift-key="${requirement.requirementKey}"]`,
    );
    const value = (name: string): string =>
      root
        ?.querySelector<HTMLInputElement | HTMLTextAreaElement>(
          `[name="${name}"]`,
        )
        ?.value.trim() ?? "";
    const sendToRecipient =
      root?.querySelector<HTMLInputElement>('[name="sendToRecipient"]')
        ?.checked === true;
    const responseValue = {
      senderFirstName: value("senderFirstName"),
      senderLastName: value("senderLastName"),
      senderEmail: value("senderEmail"),
      recipientFirstName: value("recipientFirstName"),
      recipientLastName: value("recipientLastName"),
      recipientEmail: value("recipientEmail"),
      message: value("message"),
      sendToRecipient,
    };
    const complete =
      responseValue.senderFirstName.length > 0 &&
      responseValue.senderLastName.length > 0 &&
      responseValue.senderEmail.includes("@") &&
      responseValue.recipientFirstName.length > 0 &&
      responseValue.recipientLastName.length > 0 &&
      (!sendToRecipient || responseValue.recipientEmail.includes("@"));
    this.commerce.send(
      complete
        ? {
            type: "REQUIREMENT.ANSWERED",
            response: {
              requirementKey: requirement.requirementKey,
              version: requirement.version,
              value: responseValue,
            },
          }
        : {
            type: "REQUIREMENT.CLEARED",
            requirementKey: requirement.requirementKey,
          },
    );
  }

  private renderQuestion(
    requirement: CommerceFormRequirementDto,
    question: CommerceFormRequirementDto["questions"][number],
  ): TemplateResult {
    const change = () => this.updateForm(requirement);
    const label = html`${question.label}${question.required ? " *" : ""}`;
    if (question.kind === "dropdown" || question.kind === "single-select") {
      return html`<label class="field">
        <span>${label}</span>
        <select name=${question.questionKey} @change=${change}>
          <option value="">Select…</option>
          ${question.options.map(
            ({ value }) => html`<option value=${value}>${value}</option>`,
          )}
        </select>
      </label>`;
    }
    if (question.kind === "multi-select") {
      return html`<fieldset class="field">
        <legend>${label}</legend>
        ${question.options.map(
          ({ value }) =>
            html`<label>
              <input
                type="checkbox"
                name=${question.questionKey}
                value=${value}
                @change=${change}
              />
              ${value}
            </label>`,
        )}
      </fieldset>`;
    }
    if (question.kind === "textarea" || question.kind === "signature") {
      return html`<label class="field">
        <span>${label}</span>
        <textarea name=${question.questionKey} @input=${change}></textarea>
      </label>`;
    }
    const type =
      question.kind === "date"
        ? "date"
        : question.kind === "email"
          ? "email"
          : question.kind === "phone-number"
            ? "tel"
            : "text";
    return html`<label class="field">
      <span>${label}</span>
      <input type=${type} name=${question.questionKey} @input=${change} />
    </label>`;
  }

  private renderRequirements(): TemplateResult | typeof nothing {
    const quote = this.commerce.checkoutSnapshot?.context.quote;
    if (!quote) return nothing;
    const required = quote.requirements.filter(
      ({ state }) => state === CommerceRequirementState.Required,
    );
    if (!required.length) return nothing;
    return html`<section
      class="panel stack"
      aria-labelledby="requirements-title"
    >
      <h3 id="requirements-title">Required information</h3>
      ${required.map((requirement) => {
        if (
          requirement.kind === CommerceRequirementKind.GiftDetails &&
          "minimumAmountMinor" in requirement
        ) {
          const change = () => this.updateGiftDetails(requirement);
          return html`<div
            class="stack"
            data-gift-key=${requirement.requirementKey}
          >
            <strong>Gift delivery details</strong>
            <div class="field-grid">
              <label class="field">
                <span>Your first name *</span>
                <input
                  name="senderFirstName"
                  autocomplete="given-name"
                  @input=${change}
                />
              </label>
              <label class="field">
                <span>Your last name *</span>
                <input
                  name="senderLastName"
                  autocomplete="family-name"
                  @input=${change}
                />
              </label>
            </div>
            <label class="field">
              <span>Your email *</span>
              <input
                name="senderEmail"
                type="email"
                autocomplete="email"
                @input=${change}
              />
            </label>
            <div class="field-grid">
              <label class="field">
                <span>Recipient first name *</span>
                <input name="recipientFirstName" @input=${change} />
              </label>
              <label class="field">
                <span>Recipient last name *</span>
                <input name="recipientLastName" @input=${change} />
              </label>
            </div>
            <label class="field">
              <span>Recipient email</span>
              <input name="recipientEmail" type="email" @input=${change} />
            </label>
            <label class="field">
              <span>Message</span>
              <textarea
                name="message"
                maxlength="500"
                @input=${change}
              ></textarea>
            </label>
            <label>
              <input name="sendToRecipient" type="checkbox" @change=${change} />
              Email the gift directly to the recipient
            </label>
          </div>`;
        }
        if (
          requirement.kind === CommerceRequirementKind.Form &&
          "formKey" in requirement
        ) {
          return html`<div
            class="stack"
            data-form-key=${requirement.requirementKey}
          >
            <strong>${requirement.title}</strong>
            ${
              requirement.description
                ? html`<p class="muted">${requirement.description}</p>`
                : nothing
            }
            ${requirement.questions.map((question) =>
              this.renderQuestion(requirement, question),
            )}
          </div>`;
        }
        if (isLegalAcceptanceRequirement(requirement)) {
          return html`<label>
            <input
              type="checkbox"
              @change=${(event: Event) => {
                const accepted = (event.currentTarget as HTMLInputElement)
                  .checked;
                this.commerce.send(
                  accepted
                    ? {
                        type: "REQUIREMENT.ANSWERED",
                        response: {
                          requirementKey: requirement.requirementKey,
                          version: requirement.version,
                          value: {
                            accepted: true,
                            contentHash: requirement.contentHash,
                          },
                        },
                      }
                    : {
                        type: "REQUIREMENT.CLEARED",
                        requirementKey: requirement.requirementKey,
                      },
                );
              }}
            />
            ${requirement.label}
          </label>`;
        }
        if (
          requirement.kind === CommerceRequirementKind.SpotSelection &&
          "availableSpotKeys" in requirement
        ) {
          return html`<div class="stack">
            <strong>Select a spot</strong>
            ${requirement.availableSpotKeys.map(
              (spotKey) =>
                html`<button
                  class="option"
                  type="button"
                  @click=${() =>
                    this.commerce.send({
                      type: "SPOT.SELECTED",
                      spotKey,
                      requirementKey: requirement.requirementKey,
                      version: requirement.version,
                    })}
                >
                  ${spotKey}
                </button>`,
            )}
          </div>`;
        }
        return html`<p class="muted">
          This checkout requires ${requirement.kind.replace(/_/g, " ")}.
        </p>`;
      })}
    </section>`;
  }

  private renderCommerceOptions(): TemplateResult | typeof nothing {
    const checkout = this.commerce.checkoutSnapshot;
    const quote = checkout?.context.quote;
    if (!quote) return nothing;
    return html`
      ${
        quote.accessOptions.length
          ? html`<section class="panel stack">
              <h3>How would you like to book?</h3>
              ${quote.accessOptions.map(
                (option) =>
                  html`<button
                    type="button"
                    class="option"
                    aria-pressed=${option.state === CommerceOptionState.Selected}
                    ?disabled=${option.state === CommerceOptionState.Disabled}
                    @click=${() =>
                    this.commerce.send({
                      type: "ACCESS_OPTION.CHANGED",
                      optionKey: option.optionKey,
                    })}
                  >
                    <strong>${option.label}</strong>
                    ${
                    "disabledReason" in option && option.disabledReason
                      ? html`<span class="muted"
                          >${option.disabledReason}</span
                        >`
                      : nothing
                  }
                  </button>`,
              )}
            </section>`
          : nothing
      }
      ${
        quote.paymentOptions.length
          ? html`<section class="panel stack">
              <h3>Payment method</h3>
              ${quote.paymentOptions.map(
                (option) =>
                  html`<button
                    type="button"
                    class="option"
                    aria-pressed=${option.state === CommerceOptionState.Selected}
                    ?disabled=${option.state === CommerceOptionState.Disabled}
                    @click=${() =>
                    this.commerce.send({
                      type: "PAYMENT_METHOD.CHANGED",
                      optionKey: option.optionKey,
                    })}
                  >
                    <strong>${option.label}</strong>
                    ${
                    "disabledReason" in option && option.disabledReason
                      ? html`<span class="muted"
                          >${option.disabledReason}</span
                        >`
                      : nothing
                  }
                  </button>`,
              )}
              ${
                achAuthorizationRequired(quote.paymentOptions)
                  ? html`<p class="muted" data-testid="ach-authorization">
                      By connecting your bank account, you authorize charges to
                      be debited from your account. ACH payments typically take
                      3-5 business days.
                    </p>`
                  : nothing
              }
            </section>`
          : nothing
      }
    `;
  }

  private renderTargets(): TemplateResult | typeof nothing {
    const checkout = this.commerce.checkoutSnapshot;
    const target = checkout?.context.target;
    const available = target?.availableTargets ?? [];
    if (available.length <= 1) return nothing;
    const multiAttendee =
      (this.offering?.kind === CommerceOfferingKind.Event ||
        this.offering?.kind === CommerceOfferingKind.Class ||
        this.offering?.kind === CommerceOfferingKind.Program) &&
      ((checkout?.context.selection.quantity ?? 1) > 1 ||
        (checkout?.context.selection.requestedParticipantTargetKeys.length ??
          0) > 1);
    const selectedAttendees = checkout?.context.selection
      .requestedParticipantTargetKeys.length
      ? checkout.context.selection.requestedParticipantTargetKeys
      : target?.targetKey
        ? [target.targetKey]
        : [];
    return html`<section class="panel stack" aria-labelledby="target-title">
      <h3 id="target-title">
        ${multiAttendee ? "Who is attending?" : "Who is this for?"}
      </h3>
      ${available.map(
        (option) =>
          html`<button
            type="button"
            class="option"
            aria-pressed=${
              multiAttendee
                ? selectedAttendees.includes(option.targetKey)
                : option.targetKey === target?.targetKey
            }
            @click=${() => {
              if (!multiAttendee) {
                this.commerce.send({
                  type: "TARGET.CHANGED",
                  targetKey: option.targetKey,
                });
                return;
              }
              const next = selectedAttendees.includes(option.targetKey)
                ? selectedAttendees.filter(
                    (targetKey) => targetKey !== option.targetKey,
                  )
                : [...selectedAttendees, option.targetKey];
              if (!next.length) return;
              this.commerce.send({
                type: "ATTENDEES.CHANGED",
                participantTargetKeys: next,
              });
            }}
          >
            <strong>${option.displayName}</strong>
            <span class="muted">${option.kind.replace(/_/g, " ")}</span>
          </button>`,
      )}
    </section>`;
  }

  private money(amountMinor: number, currency: string): string {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(amountMinor / 100);
  }

  private renderSummary(): TemplateResult | typeof nothing {
    const quote = this.commerce.checkoutSnapshot?.context.quote;
    if (!quote) return nothing;
    return html`<aside class="panel summary">
      <h3>Order summary</h3>
      ${summaryPriceLines(quote.price.lines).map(
        (line) =>
          html`<div class="line">
            <span>${displayFeeLabel(line.label)}</span>
            <span>${this.money(line.amountMinor, line.currency)}</span>
          </div>`,
      )}
      ${
        quote.price.tax.known
          ? html`<div class="line">
              <span>Tax</span>
              <span
                >${this.money(
                  quote.price.tax.line?.amountMinor ?? 0,
                  quote.price.tax.line?.currency ?? quote.price.dueNow.currency,
                )}</span
              >
            </div>`
          : html`<div class="line muted">
              <span>Tax</span><span>Calculated before payment</span>
            </div>`
      }
      <div class="line total">
        <span>Due now</span>
        <span
          >${this.money(
            quote.price.dueNow.amountMinor,
            quote.price.dueNow.currency,
          )}</span
        >
      </div>
      ${
        quote.policies.cancellation
          ? html`<p class="muted">${quote.policies.cancellation}</p>`
          : nothing
      }
      ${
        quote.policies.refund
          ? html`<p class="muted">${quote.policies.refund}</p>`
          : nothing
      }
    </aside>`;
  }

  private renderProviderAction(): TemplateResult | typeof nothing {
    const action = this.actionRequired();
    const checkout = this.commerce.checkoutSnapshot;
    if (!action) return nothing;
    if (action.kind === CommercePaymentActionKind.Redirect && "url" in action) {
      return html`<section class="panel stack">
        <h3>Continue to payment</h3>
        <a class="primary" href=${action.url}>Continue securely</a>
      </section>`;
    }
    return html`<section class="panel stack">
      <h3>Payment details</h3>
      <div id="commerce-payment-element"></div>
      ${
        checkout?.matches({
          awaitingExternalAction: "providerUnavailable",
        }) && checkout.context.failure?.retryable
          ? html`<button
              class="primary"
              type="button"
              @click=${() => this.commerce.send({ type: "RETRY" })}
            >
              Retry payment setup
            </button>`
          : checkout?.matches({
                awaitingExternalAction: "providerUnavailable",
              })
            ? nothing
            : html`<button
                class="primary"
                type="button"
                ?disabled=${!checkout?.matches({
                  awaitingExternalAction: "providerReady",
                })}
                @click=${() => void this.confirmProviderAction()}
              >
                ${
                  checkout?.matches({
                    awaitingExternalAction: "confirmingProvider",
                  })
                    ? "Confirming…"
                    : checkout?.matches({
                          awaitingExternalAction: "preparingProvider",
                        })
                      ? "Preparing payment…"
                      : "Confirm payment"
                }
              </button>`
      }
    </section>`;
  }

  render(): TemplateResult {
    if (!this.runtime.commerce) {
      return html`<div class="error" role="alert">
        Commerce is unavailable for this host.
      </div>`;
    }
    if (!this.offering || !this.orgId) {
      return html`<div class="error" role="alert">
        This offering cannot be identified.
      </div>`;
    }
    const surface = this.commerce.snapshot;
    const checkout = this.commerce.checkoutSnapshot;
    if (!surface || !checkout) {
      return html`<div aria-busy="true">Preparing checkout…</div>`;
    }
    if (surface.matches("succeeded")) {
      return html`<div class="success" role="status">
        <h2>Complete</h2>
        <p>Your confirmation has been recorded.</p>
      </div>`;
    }
    if (surface.matches("cancelled")) {
      return html`<div role="status">Checkout cancelled.</div>`;
    }
    const quote = checkout.context.quote;
    const decision = quote?.decision;
    const busy = (
      [
        "resolvingTarget",
        "quoting",
        "creatingIntent",
        "confirming",
        "recovering",
        "cancelling",
      ] as const
    ).some((state) => checkout.matches(state));
    return html`<div class="shell">
      <main class="stack">
        <section class="panel">
          <h2>${this.title}</h2>
          ${
            this.subtitle
              ? html`<p class="muted">${this.subtitle}</p>`
              : nothing
          }
          ${
            busy
              ? html`<p aria-live="polite" aria-busy="true">Updating…</p>`
              : nothing
          }
          ${
            decision?.status === CommerceDecisionStatus.Blocked &&
            "message" in decision
              ? html`<div class="error" role="alert">${decision.message}</div>`
              : nothing
          }
          ${
            decision?.status ===
              CommerceDecisionStatus.AuthenticationRequired &&
            "message" in decision
              ? html`<div class="error" role="alert">${decision.message}</div>`
              : nothing
          }
          ${
            checkout.context.failure
              ? html`<div class="error" role="alert">
                  ${checkout.context.failure.message}
                </div>`
              : nothing
          }
          ${
            this.allowCustomAmount
              ? html`<label class="field">
                  <span>Amount</span>
                  <input
                    type="number"
                    min="5"
                    max=${
                      this.offering.kind === CommerceOfferingKind.Gift
                        ? "1000"
                        : "2500"
                    }
                    step="0.01"
                    @change=${(event: Event) => {
                      const dollars = Number(
                        (event.currentTarget as HTMLInputElement).value,
                      );
                      this.commerce.send({
                        type: "CUSTOM_AMOUNT.CHANGED",
                        amountMinor:
                          Number.isFinite(dollars) && dollars > 0
                            ? Math.round(dollars * 100)
                            : null,
                      });
                    }}
                  />
                </label>`
              : nothing
          }
        </section>
        ${this.renderTargets()} ${this.renderCommerceOptions()}
        ${this.renderRequirements()} ${this.renderProviderAction()}
        <div class="actions">
          ${
            checkout.matches("ready")
              ? html`<button
                  class="primary"
                  type="button"
                  @click=${() => this.commerce.send({ type: "SUBMIT" })}
                >
                  ${
                    checkout.context.requestedAction ===
                    CommerceRequestedAction.JoinWaitlist
                      ? "Join waitlist"
                      : this.offering.kind === CommerceOfferingKind.Product ||
                          this.offering.kind === CommerceOfferingKind.Gift
                        ? "Purchase"
                        : "Confirm booking"
                  }
                </button>`
              : nothing
          }
          ${
            checkout.matches("failed")
              ? html`<button
                  class="primary"
                  type="button"
                  @click=${() => this.commerce.send({ type: "RETRY" })}
                >
                  Try again
                </button>`
              : nothing
          }
          ${
            checkout.context.intent?.status !== CommerceIntentStatus.Succeeded
              ? html`<button
                  class="secondary"
                  type="button"
                  ?disabled=${checkout.matches("cancelling")}
                  @click=${() => this.commerce.send({ type: "CANCEL" })}
                >
                  Cancel
                </button>`
              : nothing
          }
        </div>
      </main>
      ${this.renderSummary()}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "zipper-commerce-checkout": ZipperCommerceCheckout;
  }
}
